/**
 * Shared helpers used by every email composer in the CRM.
 *
 * Why this file exists:
 *   Five different email composers (lead, bulk, sign-link, DNO reply, template-send)
 *   each used to repeat the same text/HTML conversion + variable substitution logic
 *   inline. That made them drift apart over time. This module centralises the
 *   conversion so every composer behaves identically — keystroke-for-keystroke
 *   the same as the gold-standard `SendEmailDialog` component.
 */

// Convert a plain-text body into safe HTML so it renders in the iframe preview
// and survives an Outlook/Gmail roundtrip. Blank lines become <br>, content
// lines become <p>.
export const textToHtml = (text) => {
  if (!text) return '';
  return text
    .split('\n')
    .map((line) => (line.trim() ? `<p>${escapeHtml(line)}</p>` : '<br>'))
    .join('\n');
};

// Strip HTML back to readable text so users can flip from HTML → Text without
// seeing raw markup in the textarea.
export const htmlToText = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .trim();
};

// Minimal HTML-escape — only used when wrapping plain-text into <p>.
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Resolve a body string to the HTML representation regardless of the current
// editor mode the caller has chosen.
export const resolveBodyHtml = (body, mode) => {
  if (!body) return '';
  return mode === 'text' ? textToHtml(body) : body;
};

// Replace common customer/lead template variables. Mirrors the backend
// substitution so the in-UI preview matches the email the customer receives.
export const replaceCustomerVars = (text, ctx) => {
  if (!text || !ctx) return text || '';
  const name = (ctx.name || `${ctx.first_name || ''} ${ctx.last_name || ''}`).trim();
  const firstName = (ctx.first_name || name.split(' ')[0] || '').trim();
  const lastName = (ctx.last_name || name.split(' ').slice(1).join(' ') || '').trim();
  const vars = {
    '{{first_name}}': firstName,
    '{{last_name}}': lastName,
    '{{name}}': name || firstName,
    '{{email}}': ctx.email || '',
    '{{phone}}': ctx.phone || '',
    '{{address}}': ctx.address || '',
    '{{city}}': ctx.city || '',
    '{{postcode}}': ctx.postcode || '',
    '{{system_size}}': String(ctx.system_size || ''),
    '{{system_total_cost}}': String(ctx.system_total_cost || ''),
    '{{deposit_amount}}': String(ctx.deposit_amount || ''),
    '{{final_bill_amount}}': String(ctx.final_bill_amount || ''),
    '{{provisional_install_date}}': ctx.provisional_install_date || '',
    '{{confirmed_install_date}}': ctx.confirmed_install_date || '',
    '{{installation_type}}': ctx.installation_type || '',
    '{{status}}': ctx.status || '',
    '{{sign_link}}': ctx.sign_link || '',
    '{{expires_on}}': ctx.expires_on || '',
  };
  let result = text;
  for (const [k, v] of Object.entries(vars)) result = result.replaceAll(k, v);
  return result;
};
